import { useCallback, useMemo } from 'react';
import type { useSidePanel } from '@/components/contexts/SidePanelContext';
import type { Deadline, TaskActionHandlers } from '@/components/ui/columns-tasks';
import type { useDeadlineActions } from '@/hooks/useDeadlineActions';
import type { DeadlineTagChanges } from '@/lib/deadlines/tags';

type DeadlineActions = ReturnType<typeof useDeadlineActions>;
type OpenDocumentPanel = ReturnType<typeof useSidePanel>['openDocumentPanel'];

interface UseDeadlineHandlersArgs {
  taskActions: DeadlineActions;
  openCreateDialog: () => void;
  openEditDialog: (task: Deadline) => void;
  openDocumentPanel: OpenDocumentPanel;
  /** Opens the page's delete-confirmation dialog for a deadline id. */
  onDelete: (taskId: string) => void;
  /** Restore a soft-deleted synthetic deadline (Table "Show deleted" view only). */
  onRestore?: (syntheticId: string) => void;
}

/** Stable, memoized deadline-action callbacks + the shared handler bundle consumed
 *  by the Table columns and the row detail panel. */
export function useDeadlineHandlers({
  taskActions,
  openCreateDialog,
  openEditDialog,
  openDocumentPanel,
  onDelete,
  onRestore,
}: UseDeadlineHandlersArgs) {
  const handleCreateTask = useCallback(
    (newTask: Omit<Deadline, 'id' | 'created_by' | 'state'>) => taskActions.createDeadline(newTask),
    [taskActions],
  );
  const handleEditTask = useCallback(
    (taskId: string, updates: Partial<Deadline>) => taskActions.updateDeadline(taskId, updates),
    [taskActions],
  );
  const handleConfirmTask = useCallback((taskId: string) => taskActions.confirmDeadline(taskId), [taskActions]);
  const handleCompleteTask = useCallback((taskId: string) => taskActions.completeDeadline(taskId), [taskActions]);
  const handleMoveToInProgress = useCallback((taskId: string) => taskActions.moveToInProgress(taskId), [taskActions]);
  const handleMoveToNeedsConfirmation = useCallback(
    (taskId: string) => taskActions.moveToNeedsConfirmation(taskId),
    [taskActions],
  );
  const handleUpdateAssignee = useCallback(
    (taskId: string, assigneeGuid: string | null) => taskActions.updateAssignee(taskId, assigneeGuid),
    [taskActions],
  );
  const handleUpdateDate = useCallback(
    (taskId: string, date: Date) => taskActions.updateDate(taskId, date),
    [taskActions],
  );
  const handleUpdateParty = useCallback(
    (taskId: string, party: string) => taskActions.updateParty(taskId, party),
    [taskActions],
  );
  const handleAddTag = useCallback(
    (syntheticId: string, label: string) => taskActions.addTag(syntheticId, label),
    [taskActions],
  );
  const handleRemoveTag = useCallback(
    (syntheticId: string, tagId: string) => taskActions.removeTag(syntheticId, tagId),
    [taskActions],
  );
  const handleSaveTags = useCallback(
    (syntheticId: string, changes: DeadlineTagChanges) => taskActions.saveTags(syntheticId, changes),
    [taskActions],
  );
  const handleDocumentClick = useCallback(
    (documentGuid: string, taskId?: string) =>
      openDocumentPanel({
        documentGuid,
        activeTab: 'document' as const,
        focusCitation: taskId ? { type: 'deadlines' as const, canonicalId: taskId } : undefined,
      }),
    [openDocumentPanel],
  );

  const deadlineHandlers = useMemo<TaskActionHandlers>(
    () => ({
      onConfirm: handleConfirmTask,
      onEdit: openEditDialog,
      onComplete: handleCompleteTask,
      onDelete,
      onMoveToInProgress: handleMoveToInProgress,
      onMoveToNeedsConfirmation: handleMoveToNeedsConfirmation,
      onUpdateAssignee: handleUpdateAssignee,
      onUpdateDate: handleUpdateDate,
      onUpdateParty: handleUpdateParty,
      onDocumentClick: handleDocumentClick,
      onRestore,
      onAddTag: handleAddTag,
      onRemoveTag: handleRemoveTag,
    }),
    [
      handleConfirmTask,
      openEditDialog,
      handleCompleteTask,
      onDelete,
      handleMoveToInProgress,
      handleMoveToNeedsConfirmation,
      handleUpdateAssignee,
      handleUpdateDate,
      handleUpdateParty,
      handleDocumentClick,
      onRestore,
      handleAddTag,
      handleRemoveTag,
    ],
  );

  return {
    handleCreateTask,
    handleEditTask,
    handleConfirmTask,
    handleCompleteTask,
    handleMoveToNeedsConfirmation,
    handleUpdateAssignee,
    handleSaveTags,
    deadlineHandlers,
  };
}
